<template>
    <div class="bodyheight grid bg-gray-100 dark:bg-black relative">
        <bg-global :bgLobby="bgLobby" />

        <ul class="bodyheight overflow-auto flex flex-col gap-1 py-2 text-black dark:text-gray-100 z-10">
            <div class="uppercase text-center font-semibold mb-2">Transactions</div>
            <li v-for="(transaction, index) in transactions" :key="'transactions-' + index"
                class="flex flex-col mx-2 p-2 rounded text-sm"
                :class="index % 2 == 0 ? 'bg-black bg-opacity-10 dark:bg-white dark:bg-opacity-10' : 'dark:bg-white dark:bg-opacity-5'">
                <div class="flex items-center justify-between">
                    <div class="text-sm text-gray-700 dark:text-inner-border">{{ getDate(transaction.created_at) }}</div>
                    <div class="flex items-center gap-1 text-xs uppercase py-1 px-2 text-white drop-shadow rounded"
                        :class="tagColor(transaction.type)">
                        {{ transaction.type == 'prize' ? 'win' : transaction.type }}
                        <span v-if="transaction.type == 'jackpot'">🎉</span>
                        <span v-if="transaction.type == 'spin'">🥳</span>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <div class="text-gray-100" v-if="transaction.type != 'ticket'">Amount:</div>
                    <div class="text-gray-100" v-else>Race:</div>
                    <div class="text-white font-semibold">
                        <div v-if="transaction.type != 'ticket'" class="flex items-center gap-1">
                            <span>{{ lamportsToSol(Math.abs(transaction.amount)) }}</span>
                            <img src="/images/sol.png" class="block h-4 w-4 rounded-full" />
                        </div>
                        <div v-else>
                            {{ getRaceByValue(Math.abs(transaction.amount)) }}
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-2" v-if="transaction.tx">
                    <div class="text-xs text-gray-700 dark:text-gray-100">Transaction: {{ transaction.txShort }}</div>
                    <div>
                        <span v-if="transaction.confirmed">
                            <svg class="h-5 w-5" fill="none" viewBox="0 0 30 30">
                                <path class="fill-green-700"
                                    d="M13.474 2.80108C14.2729 1.85822 15.7271 1.85822 16.526 2.80108L17.4886 3.9373C17.9785 4.51548 18.753 4.76715 19.4892 4.58733L20.9358 4.23394C22.1363 3.94069 23.3128 4.79547 23.4049 6.0278L23.5158 7.51286C23.5723 8.26854 24.051 8.92742 24.7522 9.21463L26.1303 9.77906C27.2739 10.2474 27.7233 11.6305 27.0734 12.6816L26.2903 13.9482C25.8918 14.5928 25.8918 15.4072 26.2903 16.0518L27.0734 17.3184C27.7233 18.3695 27.2739 19.7526 26.1303 20.2209L24.7522 20.7854C24.051 21.0726 23.5723 21.7315 23.5158 22.4871L23.4049 23.9722C23.3128 25.2045 22.1363 26.0593 20.9358 25.7661L19.4892 25.4127C18.753 25.2328 17.9785 25.4845 17.4886 26.0627L16.526 27.1989C15.7271 28.1418 14.2729 28.1418 13.474 27.1989L12.5114 26.0627C12.0215 25.4845 11.247 25.2328 10.5108 25.4127L9.06418 25.7661C7.86371 26.0593 6.6872 25.2045 6.59513 23.9722L6.48419 22.4871C6.42773 21.7315 5.94903 21.0726 5.24777 20.7854L3.86969 20.2209C2.72612 19.7526 2.27673 18.3695 2.9266 17.3184L3.70973 16.0518C4.10824 15.4072 4.10824 14.5928 3.70973 13.9482L2.9266 12.6816C2.27673 11.6305 2.72612 10.2474 3.86969 9.77906L5.24777 9.21463C5.94903 8.92742 6.42773 8.26854 6.48419 7.51286L6.59513 6.0278C6.6872 4.79547 7.86371 3.94069 9.06418 4.23394L10.5108 4.58733C11.247 4.76715 12.0215 4.51548 12.5114 3.9373L13.474 2.80108Z">
                                </path>
                                <path d="M13.5 17.625L10.875 15L10 15.875L13.5 19.375L21 11.875L20.125 11L13.5 17.625Z"
                                    fill="white" stroke="white"></path>
                            </svg>
                        </span>
                        <span class="text-yellow-500" v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clip-rule="evenodd" />
                            </svg>
                        </span>
                    </div>
                    <div>
                        <a :href="'https://solscan.io/tx/' + transaction.tx + devnet" target="_blank">
                            <img class="h-5 w-5 block" src="/images/solscan.png" />
                        </a>
                    </div>
                </div>
            </li>
            <li v-if="!transactions.length"
                class="flex items-center justify-center text-sm text-red-400 dark:text-red-200 italic">
                No transactions yet!
            </li>
            <li class="flex items-center justify-center my-2">
                <button
                    class="disabled:opacity-75 py-1 px-2 text-xs uppercase rounded drop-shadow bg-gray-400 text-gray-100 dark:bg-jackpot-bg dark:text-white flex items-center gap-2"
                    :disabled="fetching" @click="loadMore" v-if="next_page <= last_page" v-cloak>
                    <span>Load More</span>
                    <spinner v-if="fetching" />
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import * as dayjs from 'dayjs'
import solana from '../mixins/solana'
import { useWallet } from 'solana-wallets-vue'

export default {
    name: 'Transactions',
    props: ['token', 'bgLobby', 'gamesList'],
    mixins: [solana],
    data() {
        return {
            fetching: false,
            transactions: [],
            next_page: 1,
            last_page: 0
        }
    },
    async mounted() {
        await this.getTransactions()
    },
    methods: {
        getRaceByValue(amount) {
            for (const game of this.gamesList.list) {
                if (game.entry === amount) {
                    return game.name;
                }
            }
            return 'Unknown'
        },
        tagColor(type) {
            switch (type) {
                case 'deposit':
                case 'prize':
                case 'refund':
                case 'jackpot':
                case 'spin':
                    return 'bg-green-600'
                    break;
                case 'ticket':
                    return 'bg-blue-600'
                    break;
                default:
                    return 'bg-red-600'
            }
        },
        getDate(date) {
            return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
        },
        async getTransactions(page = 1) {
            this.fetching = true
            axios.post('/api/account/transactions?page=' + page, {
                'wallet': useWallet().publicKey.value.toString()
            }, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((res) => {
                if (page == 1) {
                    this.transactions = []
                }
                this.transactions = [...this.transactions, ...res.data.transactions.data]
                this.last_page = res.data.transactions.last_page
                this.next_page = res.data.transactions.current_page + 1
            }).catch((err) => {
                console.log(err)
            }).then(() => {
                this.fetching = false
            })
        },
        loadMore() {
            this.getTransactions(this.next_page)
        }
    }
}
</script>
